/**
 * cookieKnown flag — tracks whether a server-backed refresh-token cookie
 * (`mochia_rt`) is expected to exist on this device.
 *
 * Decision (design.md Open Questions #3): instead of hitting /api/auth/refresh
 * on every cold load (wasting an invocation for users who have never signed
 * in), we set a lightweight localStorage flag at the moment the authorization
 * code exchange succeeds and clear it on logout. Only when the flag is present
 * does coldLoad.ts call postRefresh.
 *
 * Security note: this flag is NOT a credential. It only controls whether we
 * attempt a server round-trip. An attacker that can write to localStorage
 * already has more serious problems to worry about; and setting the flag
 * without a real cookie just results in a /api/auth/refresh that returns 401,
 * which the app handles gracefully.
 *
 * All three functions try/catch localStorage exceptions silently (private
 * mode, iOS Lockdown, storage quota — none of these should crash the auth
 * path).
 */

export const COOKIE_KNOWN_STORAGE_KEY = 'mochia.session.cookieKnown.v1';

/** Mark that a valid mochia_rt cookie should exist (set after successful exchange). */
export function setCookieKnown(): void {
  try {
    localStorage.setItem(COOKIE_KNOWN_STORAGE_KEY, '1');
  } catch {
    /* private mode / quota / iOS Lockdown — non-fatal */
  }
}

/** Remove the flag (called on logout or forced clearance). */
export function clearCookieKnown(): void {
  try {
    localStorage.removeItem(COOKIE_KNOWN_STORAGE_KEY);
  } catch {
    /* non-fatal */
  }
}

/** Returns true when the flag is set (exactly '1'). */
export function hasCookieKnown(): boolean {
  try {
    return localStorage.getItem(COOKIE_KNOWN_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}
